import { Box, Stack } from '@mui/material';
import { NoResults } from '../shared/NoResults';
import { CenteredCircularProgress } from '../../common/CenteredCircularProgress';
import { ConfigurationType, SelectedDependencyWithClearHistoryProps } from '../dependencies/types';
import { useSelector } from 'react-redux';
import { selectDependenciesConfigurationItem } from '../../../reducers/documentation/documentationReducer';
import { SearchResponse } from './types';
import { UniversalSearchList } from './UniversalSearchList';
import { getSearchListItems } from './utils';
import { UniversalSearchEmptyState } from './UniversalSearchEmptyState';
import { SingleSelectFilterPopoverOption } from '../../common/filters/single-select-filter/SingleSelectFilterPopover';
import { useUniversalSearchContext } from './UniversalSearchContext';
import { sortSearchResults } from './sortSearchResults';
import { useMemo } from 'react';
import { SearchListItem } from './types';

interface UniversalSearchContentProps {
  onListItemClick: (props: SelectedDependencyWithClearHistoryProps) => void;
  showLoader?: boolean;
  results?: SearchResponse;
  isLoadingResults?: boolean;
  crmOrgId: string;
  filterOptions: SingleSelectFilterPopoverOption[];
  onSelectedFilterItem: (key?: ConfigurationType) => void;
  selectedFilterValue?: string;
  isExpandedMode: boolean;
  renderRightElement?: (item: SearchListItem, isHovered: boolean) => React.ReactNode | null;
  getItemStyling?: (item: SearchListItem) => object;
  emptyState?: React.ReactNode;
  forceRenderIcons?: boolean;
}

export const UniversalSearchContent = ({
  onListItemClick,
  showLoader,
  results,
  isLoadingResults,
  filterOptions,
  selectedFilterValue,
  onSelectedFilterItem,
  isExpandedMode,
  crmOrgId,
  renderRightElement,
  getItemStyling,
  emptyState,
  forceRenderIcons,
}: UniversalSearchContentProps) => {
  const { searchTxt } = useUniversalSearchContext();

  const noResults = results && Object.values(results).every((result) => result?.length === 0);
  const displayList = !isLoadingResults && !noResults && searchTxt;

  const selectedDependency = useSelector(selectDependenciesConfigurationItem);
  const firstHistoryItem = selectedDependency?.history?.[0];
  const activeItemId = firstHistoryItem?.id
    ? firstHistoryItem.id
    : firstHistoryItem?.name + (firstHistoryItem?.objectName ?? '');

  const configurationItems = useMemo(
    () =>
      results ? getSearchListItems(sortSearchResults(results, crmOrgId, searchTxt)) : undefined,
    [crmOrgId, results, searchTxt],
  );

  const maybeRenderEmptyState = () => {
    if (isLoadingResults || (results && searchTxt)) {
      return null;
    }
    if (emptyState) {
      return emptyState;
    }
    return <UniversalSearchEmptyState />;
  };

  return (
    <Stack
      sx={{
        gap: 2,
        height: '100%',
        width: '100%',
        overflow: 'auto',
      }}
    >
      {isLoadingResults && <CenteredCircularProgress />}

      {!isLoadingResults && noResults && searchTxt && (
        <Box pr={1} pl={2}>
          <NoResults />
        </Box>
      )}

      {maybeRenderEmptyState()}

      {displayList && configurationItems && (
        <UniversalSearchList
          filterOptions={filterOptions}
          selectedFilterValue={selectedFilterValue}
          onSelectedFilterItem={onSelectedFilterItem}
          activeItemId={activeItemId}
          configurationItems={configurationItems}
          onListItemClick={onListItemClick}
          showLoader={showLoader}
          isExpandedMode={isExpandedMode}
          renderRightElement={renderRightElement}
          getItemStyling={getItemStyling}
          forceRenderIcons={forceRenderIcons}
        />
      )}
    </Stack>
  );
};
